import { defineStore } from "pinia";
import {
  IActivity,
  IFile,
} from "@/modules/a.worksheets/stores/worksheetDataStore";
import Delta from 'quill-delta';

//OPEN_ENDED_TYPES=[(1,'PHOTO'),(2,'VIDEO'),(3,'AUDIO'),(4,'PDF'),(5,'TEXT'),(6,'DOCUMENT')]

interface IWorksheetAnswerStore {
  uuid: string; // userWorksheetStatus.uuid
  is_wks_assessed: boolean;
  is_wks_solved: boolean;
  n_problems_solved: number;

  //max_multiple_choices: number, // is defined in worksheet in close ended answer as n_options

  allow_peer_evaluation: boolean;
  assessment_type: EvaluationScale;
  qualitative_assessment_levels: number;
  max_num_files_answer: number;
  max_photo_files_answers: number;
  max_pdf_files_answers: number;

  max_file_size_mbytes: number;
  max_duration_video_answer_seconds: number;
  max_duration_audio_answer_seconds: number;
  selectedDisplayOption: ISolutionDisplayOption; // added

  keyboard_latex: IKeyboardLatex ;

  evidence_draft: { content: Delta | null; choice: string | null };

  show_new_solution: boolean,
  show_found_error: boolean,
  keyboard: number

  shortname: string,
  permissions: IPermissions;
  answers: Array<IAnswerWrapper>;
  reviewer_name: string | null;
  student_name: string | null;
  show_private_chat_icon: boolean;
}

type Operator = [string, string];
type OperatorGroup = Operator[];
type OperatorsMatrix = OperatorGroup[];

interface IMathLiveOptions {
  showLatexMode?: boolean;
  showMenuToggle?: boolean;
  showVirtualKeyboard?: boolean;
  keyboardLayouts?: string[];
}

interface IKeyboardLatex {
  keyboard: OperatorsMatrix;
  editorOptions?: Record<string, any>;
  mathLiveOptions?: IMathLiveOptions;
}


interface IPermissions {
  canHavePeerAssess: boolean,
  canSolve: boolean;
  canSelfReview: boolean;
  canPeerReview: boolean;
  canTeacherReview: boolean;
  isPeerAssigned: boolean;
  showSelf: boolean,
  showPeer: boolean,
  showTeacher: boolean,
  showTeacherAnswers: boolean,
}

interface IAnswerWrapper {
  uuid: string;
  solved_at: number;
  problem: string;
  answer: IAnswer | null;
  teacher_solution: Array<ISolution> | null;
}

interface ISolution {
  uuid: string;
  order: number;
  isToDelete: boolean;
  solution_files: Array<IFileGroup | null>; // solution_files[0] -> Instruction, solution_files[1...n] -> Video/Media Solutions
}

interface IFileGroup {
  uuid: string;
  order: number;
  file: IFile;
  textrtf: Delta | null;
  pairwise_options: IConnection[] | null;
}

interface IConnection {
  leftId: number;
  rightId: number;
  leftUUID: string;
  rightUUID: string;
}


interface IAnswer {
  close_ended: IClosedEndedAnswer | null;
  open_ended: IOpenEndedAnswer | null;
  match_options: IMatchOptionsAnswer | null;
  words_select: IMatchOptionsAnswer | null;
  image_labels: IImageLabellingAnswer | null;
  table_fill: IMatchOptionsAnswer | null;
  sequence_ordering: ISequenceOrdering | null;
  drag_drop: IMatchOptionsAnswer | null;
  multiple_choice: IMultiChoicesGraphical | null
  // Evidência (justificação) enviada pelo aluno - vem ao nível da answer para todos os tipos.
  // Delta (objeto) ou Delta serializado
  evidences?: Delta | string | null;
}

interface IMultiChoicesGraphical {
  student_answer: Array<{
    student_choice: string[]
  }>;
  answer_assessment?: IAssessment[];
}

interface IImageLabellingAnswer {
  student_answer: Array<{
    element: string
    isCorrect: boolean
    student_choice: string
  }>;
  answer_assessment?: IAssessment[];
}

interface IMatchOptionsAnswer {
  student_answer: Array<{
    element: string
    isCorrect: boolean
    student_choice: string
  }>;
  answer_assessment?: IAssessment[];
}

interface ISequenceOrdering {
  student_answer: Array<{
    order: number
    isCorrect: boolean
    uuid: string
  }>;
  answer_assessment?: IAssessment[];
}


interface IClosedEndedAnswer {
  answer_assessment?: IAssessment[];
  correct_answer: string;
  correct_answer_pos: number;
  student_answer: string;
  is_correct: boolean;
  n_options: number;
}


interface IOpenEndedAnswer {
  uuid: string;
  input_type: InputType; //[(1,'PHOTO'),(2,'VIDEO'),(3,'AUDIO'),(4,'PDF'),(5,'TEXT'),(6,'DOCUMENT')]
  answer_files: Array<{
    uuid: string;
    order: number;
    file: IFile | null;
    textfield: string | null;
    textrtf: Delta | null;
  }>;
  answer_assessment: Array<IAssessment>;
}

interface IAssessment {
  who_reviewed: Reviewer;
  evaluation_scale: EvaluationScale;
  grade: string;
  suffix: string;
}

enum InputType {
  PHOTO = 1,
  VIDEO = 2,
  AUDIO = 3,
  PDF = 4,
  TEXT = 5,
  DOCUMENT = 6,
}
enum Reviewer {
  SELF = 1,
  PEER = 2,
  TEACHER = 3,
  SELF2 = 9,
}
enum EvaluationScale {
  QUANTITATIVE = 1,
  QUALITATIVE = 2,
}

type ISolutionDisplayOption = "Solution" | "Both" | "Question" | null;

const worksheetUserAnswerData: IWorksheetAnswerStore = {
  selectedDisplayOption: "Both",
  uuid: "73daa609-3aec-4fb9-9a90-ad1d72721d8e", //UUID of the database table UserWorksheetStatus
  is_wks_assessed: false,
  is_wks_solved: false,
  n_problems_solved: 0,
  keyboard: 0,
 
  allow_peer_evaluation: true,
  evidence_draft: { content: null, choice: null },
  keyboard_latex: {
    keyboard: [],
    mathLiveOptions: {},
    editorOptions: []
  },
  assessment_type: 1,
  qualitative_assessment_levels: 4,
  max_num_files_answer: 2,
  max_pdf_files_answers: 1,
  max_photo_files_answers: 1,
  max_file_size_mbytes: 4,
  max_duration_video_answer_seconds: 180,
  max_duration_audio_answer_seconds: 180,
  show_new_solution: false,
  show_found_error: false,
  shortname: "",
  permissions: {
    canHavePeerAssess: true,
    canSolve: true,
    canSelfReview: true,
    canPeerReview: false,
    canTeacherReview: false,
    isPeerAssigned: false,
    showSelf: false,
    showPeer: false,
    showTeacher: false,
    showTeacherAnswers: false,
  },
  answers: [
    {
      uuid: "39a856ea-ba49-4449-a42d-eca244442eee", //not to be used at this moment
      solved_at: 1671009491384,
      problem: "8f346f4d-c5eb-409a-809a-8b0392a9bccf", //uuid of the problem FK to the Problem in worksheet data
      teacher_solution: null,
      answer: {
        sequence_ordering: null,
        image_labels: null,
        words_select: null,
        drag_drop: null,
        match_options: null,
        close_ended: null,
        table_fill: null,
        multiple_choice: null,
        open_ended: {
          uuid: "",
          input_type: 5, //[(1,'PHOTO'),(2,'VIDEO'),(3,'AUDIO'),(4,'PDF'),(5,'TEXT'),(6,'DOCUMENT')]
          answer_files: [
            {
              uuid: "afcae412-a9d6-4fe9-855f-e0eb71e85585",
              order: 0,
              file: { filename: "", filetype: "", filepath: "" },
              textfield: "",
              textrtf: null
            },
          ],
          answer_assessment: [
            {
              who_reviewed: 1, //1-self, 2-peer, 3-teacher
              evaluation_scale: 1, //1-quantitative, 2-qualitative
              grade: "10",
              suffix: "points",
            },
          ],
        },
      },
    },
    {
      uuid: "b01d9673-13d1-4876-b8dd-60c937dcc124", //not to be used at this moment
      solved_at: 1671009491384,
      problem: "6090583e-a06d-46dd-acc3-11877db5ff50", //uuid of the problem FK to the Problem in worksheet data
      teacher_solution: null,
      answer: {
        sequence_ordering: null,
        image_labels: null,
        words_select: null,
        drag_drop: null,
        multiple_choice: null,
        match_options: null,
        close_ended: null,
        table_fill: null,
        open_ended: {
          uuid: "",
          input_type: 1, //[(1,'PHOTO'),(2,'VIDEO'),(3,'AUDIO'),(4,'PDF'),(5,'TEXT'),(6,'DOCUMENT')]
          answer_files: [
            {
              uuid: "4519ec59-2469-4bfa-95fc-88b32371d52e",
              order: 2,
              file: {
                filename: "1672673234040.png",
                filetype: "png",
                filepath: "documents/",
              },
              textfield: "",
              textrtf: null
            },
            {
              uuid: "d83ce747-2c7d-4c8d-829f-736ab6b11560",
              order: 1,
              file: {
                filename: "1672673229344.png",
                filetype: "png",
                filepath: "documents/",
              },
              textfield: "",
              textrtf: null
            },
          ],
          answer_assessment: [
            {
              who_reviewed: 1, //1-self, 2-peer, 3-teacher
              evaluation_scale: 1, //1-quantitative, 2-qualitative
              grade: "1",
              suffix: "points",
            },
          ],
        },
      },
    },
    {
      uuid: "cd0f67c9-800e-4ada-b884-54f354846372", //not to be used at this moment
      problem: "67983085-91e8-4c06-b740-ef384005bbe6", //uuid of the problem FK to the Problem in worksheet data
      solved_at: 1671471623591, //date time as integer
      teacher_solution: null,
      answer: {
        sequence_ordering: null,
        image_labels: null,
        words_select: null,
        multiple_choice: null,
        drag_drop: null,
        match_options: null,
        close_ended: null,
        table_fill: null,
        open_ended: {
          uuid: "",
          input_type: 1, //[(1,'PHOTO'),(2,'VIDEO'),(3,'AUDIO'),(4,'PDF'),(5,'TEXT'),(6,'DOCUMENT')]
          answer_files: [
            {
              uuid: "018048ea-5e97-47da-a4ca-9220d6d0d8ae",
              file: {
                filename: "1673442191762_JsZ61CR.png",
                filetype: "png",
                filepath: "documents/",
              },
              order: 1,
              textfield: "",
              textrtf: null
            },
          ],
          answer_assessment: [
            {
              who_reviewed: 1, //1-self, 2-peer, 3-teacher
              evaluation_scale: 1, //1-quantitative, 2-qualitative
              grade: "2",
              suffix: "points",
            },
          ],
        },
      },
    },
    {
      uuid: "62f6a9e9-3539-4e1c-8985-9c16e7707ccc",
      problem: "e25fdb75-d0fd-48a7-993d-478cad4e9c64",
      solved_at: 1671480784153,
      teacher_solution: null,
      answer: {
        sequence_ordering: null,
        image_labels: null,
        multiple_choice: null,
        words_select: null,
        match_options: null,
        drag_drop: null,
        close_ended: null,
        table_fill: null,
        open_ended: {
          uuid: "",
          input_type: 1,
          answer_files: [
            {
              uuid: "4b3925f3-a0fb-4cde-bbae-627f9e60315e",
              file: {
                filename: "1671527762803.png",
                filetype: "png",
                filepath: "documents/",
              },
              order: 3,
              textfield: "",
              textrtf: null,
            },
            {
              uuid: "1787ef8b-c9e7-47f3-b39b-0a37dd6a749c",
              file: {
                filename: "1671527748446.png",
                filetype: "png",
                filepath: "documents/",
              },
              order: 1,
              textfield: "",
              textrtf: null,
            },
            {
              uuid: "d4407553-15d8-4594-b88b-6330550eb6d8",
              file: {
                filename: "1671527753052.png",
                filetype: "png",
                filepath: "documents/",
              },
              order: 2,
              textfield: "",
              textrtf: null,
            },
          ],
          answer_assessment: [
            {
              who_reviewed: 1, //1-self, 2-peer, 3-teacher
              evaluation_scale: 1, //1-quantitative, 2-qualitative
              grade: "5",
              suffix: "points",
            },
          ],
        },
      },
    },
    {
      uuid: "f59b91a8-fb69-4412-b5ab-ae0cffb35a63",
      problem: "84823e84-70dc-4813-aaa4-92d3339ea96f",
      solved_at: 1671553113244,
      teacher_solution: null,
      answer: {
        sequence_ordering: null,
        image_labels: null,
        words_select: null,
        drag_drop: null,
        match_options: null,
        multiple_choice: null,
        close_ended: null,
        table_fill: null,
        open_ended: {
          uuid: "",
          input_type: 5,
          answer_files: [
            {
              uuid: "c0acd506-9c40-4ee8-8cd4-b2090c2d7ac4",
              file: { filename: "", filetype: "", filepath: "", },
              order: 0,
              textfield: "",
              textrtf: null,
            },
          ],
          answer_assessment: [],
        },
      },
    },
  ],
  reviewer_name: null,
  student_name: null,
  show_private_chat_icon: false,
};

function hasAnswerContent(wrapped?: IAnswerWrapper): boolean {
  const answer = wrapped?.answer as unknown;
  if (answer === undefined || answer === null) return false;
  if (Array.isArray(answer)) return answer.length > 0;
  if (typeof answer === "object") return Object.keys(answer as object).length > 0;
  return true;
}

const useUserAnswersData = defineStore({
  id: "WksUserAnswerDataModule",
  state: (): IWorksheetAnswerStore => worksheetUserAnswerData,
  getters: {
    hasEvidence: (state): boolean =>
      !!(state.evidence_draft.content && state.evidence_draft.content.ops.length > 0),
    /** Delta (objeto) enviado no pedido como evidência */
    evidencePayload: (state): Delta | undefined =>
      state.evidence_draft.content && state.evidence_draft.content.ops.length > 0
        ? state.evidence_draft.content
        : undefined,
    getAllAnswers: (state): Array<IAnswerWrapper> => {
      const completedActivities = state.answers.filter(hasAnswerContent);
      return completedActivities ? completedActivities : [];
    },
    isAnswer:
      (state) =>
        (problemUUID: string): boolean => {
          const answer = state.answers.find(
            (answer) => answer.problem === problemUUID
          );
          return hasAnswerContent(answer);
        },
    getAnswer:
      (state) =>
        (problemUUID: string): IAnswerWrapper | undefined => {
          return state.answers.find(
            (activity) => activity.problem === problemUUID
          );
        },
  },
  actions: {
    async worksheetStatus(wks_uuid: string, usr_uuid: string) {
      //const resp = getWorksheetStatus.get(wks_uuid, usr_uuid)
      return 0;
    },
    deleteAnswer(answerUUID: string) {
      this.answers = this.answers.filter(
        (answer) => answer.uuid === answerUUID
      );
    },
    setEvidenceContent(content: Delta | null) {
      this.evidence_draft.content = content;
    },
    setEvidenceChoice(choice: string | null) {
      this.evidence_draft.choice = choice;
    },
    clearEvidence() {
      this.evidence_draft = { content: null, choice: null };
    },
    setAnswer(wrappedAnswer: IAnswerWrapper) {
      const index = this.answers.findIndex((answer) => answer.problem === wrappedAnswer.problem);
      if (index === -1) {
        this.answers.push(wrappedAnswer);
        return;
      }

      const previous = this.answers[index];
      this.answers.splice(index, 1, {
        ...wrappedAnswer,
        teacher_solution: wrappedAnswer.teacher_solution?.length
          ? wrappedAnswer.teacher_solution
          : previous.teacher_solution,
      });
    },
    reset() {
      this.answers = [];
      this.permissions = {
        canHavePeerAssess: true,
        canSolve: true,
        canSelfReview: true,
        canPeerReview: false,
        canTeacherReview: false,
        isPeerAssigned: false,
        showSelf: false,
        showPeer: false,
        showTeacher: false,
        showTeacherAnswers: false
      };
    },
  },
});

export {
  useUserAnswersData,
  IWorksheetAnswerStore,
  ISolutionDisplayOption,
  IAnswerWrapper,
  IAnswer,
  IPermissions,
  IAssessment,
  Reviewer,
  EvaluationScale,
  IOpenEndedAnswer,
  IClosedEndedAnswer,
  IMatchOptionsAnswer,
};
